/*
 * WaWiReservierungenApp3.tsx Anzeige und Bearbeiten von Blockaufträgen.
 *
 * Created by Dr. Maximillian Dornseif 2020-09-03
 * Copyright (c) 2020-2023 HUDORA GmbH
 */

import {
  DefaultButton,
  Dialog,
  DialogFooter,
  PrimaryButton,
  Separator,
  Spinner,
  SpinnerSize,
} from '@fluentui/react'
import { useDialog } from 'fluentui-hooks'
import React from 'react'
import toast from 'react-hot-toast'
import { useMutation, useQuery } from 'urql'

import { GruppenCard } from '../../lib/components/GruppenCard'
import { IEntitySubPage3 } from '../../lib/components/ke/EntityPage3'
import { EntityPageInner3 } from '../../lib/components/ke/EntityPageInner3'
import { KindListe3 } from '../../lib/components/ke/KindListe3'
import { KindPage3 } from '../../lib/components/ke/KindPage3'
import { KundenCard } from '../../lib/components/KundenCard'
import { ReservierungsPositionen } from './ReservierungsPositionen'
import {
  ABRUF_LIST_QUERY,
  AUDIT_LIST_QUERY,
  LIST_QUERY,
  PATCH_MUTATION,
  POSITIONEN_QUERY,
  RESERVIERUNG_STORNO_MUTATION,
  RESEVIERUNG_DETAILS_QUERY,
  RESEVIERUNG_QUERY,
  STORNO_LIST_QUERY,
} from './WaWiReservierungenQueries'

export const WaWiReservierungenApp = () => {
  const [openDialog, dialogProps, , dismissDialog] = useDialog(
    'Blockauftrag Stornieren',
    'Wirklich stornieren?'
  )

  return (
    <KindPage3
      basePath={'/wawi/ic'}
      componentPath={'rk'}
      title={'Blockaufträge'}
      schemaName={'HwReservierungsKopf'}
      hideExcept={[
        'reservierungsnr',
        'empfaengernr',
        'gruppe.name',
        'kunde.name',
        'anlieferdatum_von',
        'anlieferdatum_bis',
        'name',
      ]}
      // suggestionQuery={SUGGESTIONS_QUERY}
      listQuery={LIST_QUERY}
      entityQuery={RESEVIERUNG_QUERY}
      subPages={subPages}
      renderEntityPage={(e) => {
        const commandBarItems = !e?.erledigt
          ? [
              {
                key: 'stornieren',
                text: 'stornieren',
                ariaLabel: 'Stornieren',
                iconProps: {
                  iconName: 'Delete',
                },
                iconOnly: false,
                onClick: openDialog,
              },
            ]
          : []

        return e?.designator ? (
          <EntityPageInner3
            entity={e}
            schemaName={'HwReservierungsKopf'}
            mutation={PATCH_MUTATION}
            commandBarItems={commandBarItems}
            renderRightSidebar={(e, s) => (
              <>
                <KundenCard nr={e?.empfaengernr} />
                <GruppenCard nr={e?.empfaengernr} />
              </>
            )}
            renderBottom={(e, s) => (
              <>
                <DisplayDetails entity={e} />
                <Dialog {...dialogProps}>
                  <StornoDialogInner reservierung={e} onDismiss={dismissDialog}></StornoDialogInner>
                </Dialog>
              </>
            )}
          />
        ) : null
      }}
    ></KindPage3>
  )
}

function DisplayDetails({ entity }) {
  const [result] = useQuery({ query: RESEVIERUNG_DETAILS_QUERY, variables: { nr: entity.designator } })
  // Die Positionen haben wir ja schon mit dem Kopf gelsen, aber jetzt wollen wir noch die Artikel dazu
  return <ReservierungsPositionen auftrag={entity} nr={entity.designator} gqlQuery={POSITIONEN_QUERY} />
}

export function StornoDialogInner({ reservierung, onDismiss }) {
  const [mutationResult, mutateEntity] = useMutation(RESERVIERUNG_STORNO_MUTATION)
  const { fetching } = mutationResult

  // Daten per GraphQL Mutation an den Server senden.
  const uebertragen = async () => {
    const result = await mutateEntity({
      keyStr: reservierung._keyStr,
      designator: reservierung.designator,
    } as any)
    if (result.error) {
      toast.error(`Fehler: ${result.error.message}`)
    } else {
      toast.success('Erfolgreich storniert, bitte jetzt die Seite neu laden!')
    }
    onDismiss()
  }

  return (
    <>
      <Separator></Separator>
      <div>{fetching ? <Spinner size={SpinnerSize.xSmall} /> : null}</div>

      <DialogFooter>
        <PrimaryButton onClick={uebertragen} text="Stornieren" disabled={fetching} />
        <DefaultButton onClick={onDismiss} text="Schießen" />
      </DialogFooter>
    </>
  )
}

const subPages: IEntitySubPage3[] = [
  {
    title: 'Stornos',
    path: 'storno',
    render: (entity) => (
      <KindListe3
        title={'Stornos'}
        schemaName={'HwReservierungsPosStorno'}
        listQuery={STORNO_LIST_QUERY}
        listQueryVars={{ nr: entity.designator }}
        hideExcept={[
          'designator',
          'menge_storniert',
          'bartnr',
          'grund',
          'erfasst_von',
          'created_at',
          // "reservierungsnr"
        ]}
      />
    ),
  },
  {
    title: 'Abrufe',
    path: 'abruf',
    render: (entity) => (
      <KindListe3
        title={'Abrufe'}
        schemaName={'HwReservierungsAbruf'}
        listQuery={ABRUF_LIST_QUERY}
        listQueryVars={{ nr: entity.designator }}
      />
    ),
  },
  {
    title: 'Audit',
    path: 'audit',
    render: (entity) => (
      <KindListe3
        title={'Audit'}
        schemaName={'HwAuditLog'}
        listQuery={AUDIT_LIST_QUERY}
        listQueryVars={{ nr: entity.designator }}
        hideExcept={['object_name', 'level', 'message', 'created_at', 'created_by2', 'gae_version']}
      />
    ),
  },
]
